import { Fragment, lazy , useEffect, useState} from 'react';
import { Col, Row } from 'react-bootstrap';
import { BlockBody, BlockContainer, BlockHeader } from '@medifind/containers';
import { MFLazyLoad, ReadMore, Skeleton } from '@medifind/shared-basic-components';
import { genderLookup, getNullList, useMediaQueryDown } from '@medifind/utils';
import styles from './DoctorProfileOverview.module.scss';
const YoutubeVideo = lazy(() => import('@medifind/lazy-loadable/lib/shared-basic-components/LazyYoutubeVideo'));

const DoctorProfileOverview = ({ doctor, classes }) => {
  const {
    awards,
    affiliations,
    credentials,
    specialties,
    demographics,
    languages,
    residencyInstitution,
    fellowships,
    biography,
    youtubeVideos,
  } = doctor || {};
  const { hospitals = [] } = affiliations || {};
  const { gradInstitution, licenses, boardCertifications } = credentials || {};

  const isMobileXS = useMediaQueryDown('xs');
  const youtubeContainerHeight = (window.innerWidth > 670 ? 640 : window.innerWidth - 30) * 9 / 16; // to scroll directly to #insurance section

  return (
    <BlockContainer id="overview" className={classes.block}>
      <BlockHeader priority={2} style={{ zIndex: 2 }} className={classes.blockHeader}>
        Overview
      </BlockHeader>

      {/* youtube videos */}
      {youtubeVideos?.length > 0 ? (
        <MFLazyLoad height={youtubeContainerHeight}>
          <YoutubeVideo title={youtubeVideos?.[0]?.title} youtubeId={youtubeVideos?.[0]?.videoId} />
        </MFLazyLoad>
      ) : null}

      <BlockBody className={classes.blockBody}>
        {!doctor && (
          <div className={styles['biography']}>
            <p>
              <Skeleton count={3} />
            </p>
            <p>
              <Skeleton count={2} />
            </p>
          </div>
        )}
        {doctor &&
          biography &&
          (isMobileXS ? (
            <ReadMore collapsedHeight={100} height={100} className={styles['biography__read-more']}>
              <div className={styles['biography']} dangerouslySetInnerHTML={{ __html: biography }}></div>
            </ReadMore>
          ) : (
            <div className={styles['biography']} dangerouslySetInnerHTML={{ __html: biography }}></div>
          ))}

        <Row className={styles['credentials']}>
          <Col
            md={6}
            lg={12}
            xl={6}
            style={{
              display:
                (gradInstitution?.name && gradInstitution?.gradYearNumber) ||
                specialties?.length > 0 ||
                licenses?.length > 0 ||
                boardCertifications?.length > 0
                  ? 'block'
                  : 'none',
            }}
          >
            {/* Graduate Institution */}
            {gradInstitution?.name || gradInstitution?.gradYearNumber || !doctor ? (
              <div>
                <div className={styles['credentials__header']}>Graduate {gradInstitution?.name ? 'Institution' : 'Year'}</div>
                {gradInstitution?.name || gradInstitution?.gradYearNumber ? (
                  <>
                    <div className={styles['credentials__credential-item']}>
                      {gradInstitution?.name ? gradInstitution.name : null}
                      {gradInstitution?.name && gradInstitution?.gradYearNumber ? ',' : null}{' '}
                      {gradInstitution?.gradYearNumber ? gradInstitution.gradYearNumber : null}
                    </div>
                  </>
                ) : (
                  <Skeleton count={2} />
                )}
              </div>
            ) : null}
            {/* Residency Institution */}
            {residencyInstitution?.name || !doctor ? (
              <div>
                <div className={styles['credentials__header']}>Residency</div>
                <div className={styles['credentials__credential-item']}>
                  {residencyInstitution?.name ? residencyInstitution.name : <Skeleton />}
                </div>
              </div>
            ) : null}
            {/* Specialties */}
            {specialties?.length > 0 || !doctor ? (
              <div>
                {(specialties?.length > 0 ? specialties : getNullList(3)).map((s, i) => (
                  <Fragment key={'specialties-' + i}>
                    {i === 0 && <div className={styles['credentials__header']}>Specialties</div>}
                    <div className={styles['credentials__credential-item']}>{s ? s : <Skeleton />}</div>
                  </Fragment>
                ))}
              </div>
            ) : null}
            {/* Licenses */}
            {licenses || !doctor ? (
              <div>
                {(licenses ? licenses : getNullList(1)).map((l, i) => (
                  <Fragment key={'licenses-' + i}>
                    {i === 0 && <div className={styles['credentials__header']}>Licenses</div>}
                    <div className={styles['credentials__credential-item']}>{l ? l : <Skeleton />}</div>
                  </Fragment>
                ))}
              </div>
            ) : null}
            {/* Board Certs */}
            {boardCertifications || !doctor ? (
              <div>
                {(boardCertifications ? boardCertifications : getNullList(3)).map((b, i) => (
                  <Fragment key={'board-certificate-' + i}>
                    {i === 0 && <div className={styles['credentials__header']}>Board Certifications</div>}
                    <div className={styles['credentials__credential-item']}>{b ? b : <Skeleton />}</div>
                  </Fragment>
                ))}
              </div>
            ) : null}
            {/* Fellowship */}
            {fellowships || !doctor ? (
              <div>
                {(fellowships ? fellowships : getNullList(3)).map((b, i) => (
                  <Fragment key={'fellowships-' + i}>
                    {i === 0 && <div className={styles['credentials__header']}>Fellowships</div>}
                    <div className={styles['credentials__credential-item']}>{b ? b : <Skeleton />}</div>
                  </Fragment>
                ))}
              </div>
            ) : null}
          </Col>
          <Col md={6} lg={12} xl={6}>
            {/* Hospital Affiliations */}
            {hospitals?.length > 0 || !doctor ? (
              <div>
                {(hospitals?.length > 0 ? hospitals : getNullList(3)).map((a, i) => (
                  <Fragment key={'hospital-' + i}>
                    {i === 0 && <div className={styles['credentials__header']}>Hospital Affiliations</div>}
                    <div className={styles['credentials__credential-item']}>{a?.name ? a.name : <Skeleton />}</div>
                  </Fragment>
                ))}
              </div>
            ) : null}

            {/* Languages Spoken */}
            {languages || !doctor ? (
              <div>
                {/* <div className={styles['credentials__header']}>Languages Spoken</div> */}
                {(languages || getNullList(2)).map((l, i) => (
                  <Fragment key={'languages-' + i}>
                    {i === 0 && <div className={styles['credentials__header']}>Languages Spoken</div>}
                    <div className={styles['credentials__credential-item']}>{l ? l : <Skeleton />}</div>
                  </Fragment>
                ))}
              </div>
            ) : null}

            {/* Gender */}
            {demographics?.sex || !demographics ? (
              <div>
                <div className={styles['credentials__header']}>Gender</div>
                <div className={styles['credentials__credential-item']}>
                  {demographics?.sex ? genderLookup(demographics.sex) : <Skeleton />}
                </div>
              </div>
            ) : null}

            {/* Awards */}
            {awards || !doctor ? (
              <div>
                {(awards || getNullList(2)).map((l, i) => (
                  <Fragment key={'awards-' + i}>
                    {i === 0 && <div className={styles['credentials__header']}>Awards</div>}
                    <div className={styles['credentials__credential-item']}>{l ? l : <Skeleton />}</div>
                  </Fragment>
                ))}
              </div>
            ) : null}
          </Col>
        </Row>
      </BlockBody>
    </BlockContainer>
  );
};

export { DoctorProfileOverview as default, DoctorProfileOverview };
